import { Suspense, useState } from "react";
import { NavLink, Outlet } from "react-router";
import { IS_DEV } from "../config";
import { useLogout } from "../query/auth";
import { useAuthStore } from "../store/auth";
import { ConnDot } from "./ConnDot";

// Иконки nav — порт SVG-путей из web/index.html.
const ICON: Record<string, string> = {
  dash: "M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z",
  strategies:
    "M12 2a2 2 0 0 1 2 2v1h3a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h3V4a2 2 0 0 1 2-2zM9 12a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z",
  lab: "M9 2v6.5L4.2 17a3 3 0 0 0 2.6 4.5h10.4A3 3 0 0 0 19.8 17L15 8.5V2H9zm2 2h2v5l1.2 2.1h-4.4L11 9V4z",
  runs: "M4 4h16v3H4V4zm0 6.5h16v3H4v-3zM4 17h16v3H4v-3z",
  market:
    "M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7-7a2 2 0 0 1-.6-1.4V4a2 2 0 0 1 2-2h7.8a2 2 0 0 1 1.4.6l6.4 6.4a2 2 0 0 1 0 2.8zM7.5 7.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
  settings:
    "M12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zm7.43-2.53c.04-.32.07-.64.07-.97s-.03-.65-.07-.97l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.61-.22l-2.49 1a7.3 7.3 0 0 0-1.69-.98l-.38-2.65A.49.49 0 0 0 14 2h-4a.49.49 0 0 0-.49.42l-.38 2.65a7.6 7.6 0 0 0-1.69.98l-2.49-1a.5.5 0 0 0-.61.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65c-.04.32-.07.65-.07.97s.03.65.07.97l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46c.14.24.42.32.61.22l2.49-1c.52.39 1.08.73 1.69.98l.38 2.65c.05.24.25.42.49.42h4c.24 0 .44-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.24.09.51 0 .61-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65z",
  admin: "M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4zm0 6a2.4 2.4 0 0 0-1 4.6V15h2v-2.4A2.4 2.4 0 0 0 12 8z",
};

const Ico = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" className="ico" aria-hidden="true">
    <path d={d} />
  </svg>
);

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}
const NAV: NavItem[] = [
  { to: "/", label: "Дашборд", icon: "dash", end: true },
  { to: "/strategies", label: "Стратегии", icon: "strategies" },
  { to: "/single", label: "Лаборатория", icon: "lab" },
  { to: "/runs", label: "Бэктесты", icon: "runs" },
  { to: "/marketplace", label: "Маркетплейс", icon: "market" },
  { to: "/settings", label: "Настройки", icon: "settings" },
];

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const [navOpen, setNavOpen] = useState(false);
  const initials = (user?.username || "?").slice(0, 2).toUpperCase();
  const showAdmin = !!user?.is_admin && IS_DEV;

  return (
    <>
      <header className="app-header">
        <NavLink className="brand" to="/" aria-label="kquant — на дашборд">
          <img className="brand-mark" src="/logo.svg" alt="" width={24} height={24} />
          <span className="brand-name">kquant</span>
        </NavLink>

        <nav className={`nav${navOpen ? " open" : ""}`} id="nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
              onClick={() => setNavOpen(false)}
            >
              <Ico d={ICON[n.icon]} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {showAdmin ? (
            <NavLink
              to="/admin"
              className={({ isActive }) => `nav-link admin-link${isActive ? " active" : ""}`}
              onClick={() => setNavOpen(false)}
            >
              <Ico d={ICON.admin} />
              <span>Админка</span>
            </NavLink>
          ) : null}
        </nav>

        <div className="header-right">
          <ConnDot />
          <NavLink className="avatar" to="/settings" title="Профиль и настройки" aria-label="Профиль и настройки">
            {initials}
          </NavLink>
          <button
            type="button"
            className="icon-btn"
            title="Выйти из аккаунта"
            aria-label="выйти"
            onClick={() => logout.mutate()}
          >
            <svg viewBox="0 0 24 24" className="ico" aria-hidden="true">
              <path d="M16 17v-2H9v-2h7V9l4 4-4 4M14 2a2 2 0 0 1 2 2v2h-2V4H5v16h9v-2h2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9z" />
            </svg>
          </button>
          <button
            type="button"
            className="nav-burger"
            aria-label="меню"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" className="ico" aria-hidden="true">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
            </svg>
          </button>
        </div>
      </header>

      <main id="app">
        <Suspense
          fallback={
            <section className="page show">
              <div className="card" style={{ padding: 24 }}>
                <span className="muted">Загрузка…</span>
              </div>
            </section>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </>
  );
}
